/* Public @username card — /u/:username (no auth). */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Shield, Flame, Crown, TrendingUp, Lock, CheckCircle2 } from "lucide-react";
import { fmtMoney } from "@/lib/api";

const LEVEL_COLORS = { 1: "rgb(var(--c-sage))", 2: "rgb(var(--c-gold))", 3: "rgb(var(--c-forest))", 4: "#2F7F8A", 5: "rgb(var(--c-ink))" };

export default function PublicProfile() {
  const { username } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/profile/${encodeURIComponent(username)}`)
      .then(({ data }) => setP(data)).catch((e) => setErr(e.response?.data?.detail || "Profile not found"));
  }, [username]);
  const initials = (p?.name || username || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="public-profile-page">
      <header className="border-b border-sand">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand"><Shield className="w-4 h-4 text-forest" /><span className="font-serif text-lg">Withdraw Guard</span></Link>
          <Link to="/register" className="text-xs uppercase tracking-[0.2em] font-semibold text-forest" data-testid="public-join">Level up too →</Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-12">
        {err && <div className="text-center text-moss" data-testid="public-profile-error">{err}</div>}
        {p && (
          <div className="rounded-3xl bg-ink text-cream p-8 relative overflow-hidden" data-testid="public-profile-card">
            <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-transparent pointer-events-none" />
            <div className="relative flex items-center gap-5">
              {p.avatar ? <img src={p.avatar} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-cream" data-testid="public-avatar" />
                : <span className="w-20 h-20 rounded-full bg-forest inline-flex items-center justify-center font-serif text-3xl">{initials}</span>}
              <div className="min-w-0">
                <div className="font-mono text-sm text-gold" data-testid="public-username">@{p.username}</div>
                <h1 className="font-serif text-3xl leading-tight truncate">{p.name}</h1>
                <div className="text-[11px] text-sage-light mt-1 flex items-center gap-2 flex-wrap">
                  Member since {p.member_since}
                  {p.founding_member && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold text-white text-[9px] uppercase tracking-wider font-semibold"><Crown className="w-2.5 h-2.5" /> Founding Member</span>}
                </div>
              </div>
            </div>
            <div className="relative mt-8 grid sm:grid-cols-3 gap-3">
              {p.level && (
                <div className="rounded-2xl bg-cream/5 border border-cream/10 p-4" data-testid="public-level">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-gold inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Level</div>
                  <div className="flex items-end gap-2 mt-1"><span className="font-mono text-4xl leading-none">{p.level.level}</span><span className="font-serif text-lg">{p.level.name}</span></div>
                  <div className="flex gap-1 mt-2">{[1, 2, 3, 4, 5].map((n) => <span key={n} className="h-1 w-5 rounded-full" style={{ background: n <= p.level.level ? LEVEL_COLORS[p.level.level] : "rgb(var(--c-ink-soft))" }} />)}</div>
                </div>
              )}
              {p.streaks && (
                <div className="rounded-2xl bg-cream/5 border border-cream/10 p-4" data-testid="public-streaks">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-gold inline-flex items-center gap-1"><Flame className="w-3 h-3" /> Streaks</div>
                  {p.streaks.length === 0 ? <div className="text-sm text-sage-light mt-2">Building the first one.</div> : (
                    <ul className="mt-2 space-y-1">{p.streaks.slice(0, 4).map((s) => (
                      <li key={s.name} className="flex items-center justify-between text-sm gap-2"><span className="capitalize truncate">{s.name}</span>
                        <span className={`font-mono text-xs px-1.5 py-0.5 rounded-full ${s.months >= 3 ? "bg-clay text-white" : "bg-cream/10"}`}>{s.months} mo</span></li>))}
                    </ul>)}
                  <div className="text-[10px] text-sage-light mt-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {p.locks_paid} lock group{p.locks_paid === 1 ? "" : "s"} paid off</div>
                </div>
              )}
              {p.networth && (
                <div className="rounded-2xl bg-cream/5 border border-cream/10 p-4" data-testid="public-networth">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-gold inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Lifetime earnings</div>
                  <div className="font-mono text-2xl mt-1">{p.networth.total != null ? fmtMoney(p.networth.total) : "—"}</div>
                  <div className="text-[10px] text-sage-light mt-1">{fmtMoney(p.networth.vaults_sealed)} sealed in vaults</div>
                </div>
              )}
            </div>
            <div className="relative mt-6 text-[10px] text-sage">Shared from Withdraw Guard · numbers chosen by the member</div>
          </div>
        )}
      </main>
    </div>
  );
}
